import { useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { User, Shield, Bell, Key, Users } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('profile');
  const [profile, setProfile] = useState({ fullName: user?.fullName || '', email: user?.email || '', company: user?.company || '', timezone: 'America/New_York' });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'api', label: 'API Keys', icon: Key },
    { id: 'teams', label: 'Teams', icon: Users },
  ];

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-white font-mono">Settings</h1>
      <div className="flex gap-1 border-b border-zinc-800 pb-px">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-mono transition-all border-b-2 ${tab === t.id ? 'text-emerald-400 border-emerald-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'profile' && (
        <div className="max-w-xl space-y-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-sm bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-mono text-2xl font-bold">{profile.fullName.charAt(0)}</div>
            <button className="text-xs font-mono text-emerald-400 hover:underline">Upload Avatar</button>
          </div>
          {[
            { label: 'Full Name', key: 'fullName', type: 'text' },
            { label: 'Email', key: 'email', type: 'email' },
            { label: 'Company', key: 'company', type: 'text' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">{f.label}</label>
              <input data-testid={`settings-${f.key}`} type={f.type} value={profile[f.key]} onChange={e => setProfile({ ...profile, [f.key]: e.target.value })} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none" />
            </div>
          ))}
          <button data-testid="settings-save-btn" onClick={() => toast.success('Profile updated successfully')} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale">Save Changes</button>
        </div>
      )}

      {tab === 'security' && (
        <div className="max-w-xl space-y-6">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-white font-bold mb-4">Change Password</h3>
            <div className="space-y-3">
              <input data-testid="settings-current-pw" type="password" placeholder="Current password" className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
              <input data-testid="settings-new-pw" type="password" placeholder="New password" className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
              <button className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-sm rounded-sm px-4 py-2 hover:bg-emerald-500/20 transition-all" onClick={() => toast.success('Password updated')}>Update Password</button>
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-white font-bold mb-2">Two-Factor Authentication</h3>
            <p className="text-xs text-zinc-500 mb-4">Add an extra layer of security to your account.</p>
            <button data-testid="settings-enable-2fa" onClick={() => toast.success('2FA setup initiated — check your authenticator app')} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-sm rounded-sm px-4 py-2 hover:bg-emerald-500/20 transition-all">Enable 2FA</button>
          </div>
        </div>
      )}

      {tab === 'notifications' && (
        <div className="max-w-xl space-y-4">
          {['Instance alerts', 'Billing notifications', 'Product updates', 'Weekly digest', 'Security alerts'].map((n, i) => (
            <div key={i} className="flex items-center justify-between bg-[#0A0A0A] border border-zinc-800 rounded-sm p-4">
              <span className="text-sm font-mono text-zinc-300">{n}</span>
              <button data-testid={`notif-toggle-${i}`} className="w-10 h-5 rounded-full bg-emerald-500 relative">
                <div className="w-4 h-4 rounded-full bg-white absolute right-0.5 top-0.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'api' && (
        <div className="max-w-xl space-y-4">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-white font-bold mb-4">API Keys</h3>
            <div className="bg-[#111] border border-zinc-800 rounded-sm p-3 font-mono text-sm text-zinc-500 mb-4">cs_live_••••••••••••••••••••••4f2a</div>
            <button data-testid="generate-api-key" onClick={() => toast.success('New API key generated')} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-sm rounded-sm px-4 py-2 hover:bg-emerald-500/20 transition-all">Generate New Key</button>
          </div>
        </div>
      )}

      {tab === 'teams' && (
        <div className="max-w-xl space-y-4">
          <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-mono text-white font-bold mb-4">Team Members</h3>
            <div className="flex items-center gap-3 p-3 bg-[#111] rounded-sm mb-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-mono text-sm font-bold">A</div>
              <div className="flex-1"><p className="text-sm font-mono text-white">{profile.fullName}</p><p className="text-xs text-zinc-600">Owner</p></div>
            </div>
            <button data-testid="invite-member-btn" onClick={() => toast.success('Invitation sent')} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-sm rounded-sm px-4 py-2 hover:bg-emerald-500/20 transition-all">Invite Member</button>
          </div>
        </div>
      )}
    </div>
  );
}
